// Walk above the project root looking for stray lockfiles that could trip
// Next.js 15's "shallowest lockfile wins" workspace-root detection. When the
// shallowest pick is a stray lockfile in a parent directory (a common cause:
// a leftover `~/package-lock.json` from a one-off `npm install` in the home
// dir), Next.js walks that entire tree for module tracing and the dev
// server can OOM under load. Backlog 353. Defensive-only — never blocks.

import { existsSync as defaultExistsSync } from 'node:fs';
import { homedir as defaultHomedir } from 'node:os';
import { dirname, join, resolve } from 'node:path';

const LOCKFILE_NAMES = [
  'package-lock.json',
  'pnpm-lock.yaml',
  'yarn.lock',
  'bun.lockb',
];

export interface ParentLockfileCheckDeps {
  /** Test override — defaults to `os.homedir()`. */
  homedir?: () => string;
  /** Test override — defaults to `fs.existsSync`. */
  existsSync?: (p: string) => boolean;
}

/**
 * Return absolute paths to lockfiles found in directories *above* `projectRoot`
 * and *at or below* the user's home directory. Deepest-first.
 *
 * Stop walking when any of these is reached (each stop dir is checked first):
 *   1. the user's home directory (inclusive)
 *   2. a directory containing its own `.git/` — a foreign repo boundary
 *   3. the filesystem root
 *
 * If the project is outside the user's home tree, returns `[]` — we only
 * care about strays in the user's personal tree.
 *
 * Errors from `existsSync` (permission denied, symlink loops, EACCES) are
 * swallowed per-path so a single bad stat never crashes the dev runner.
 */
export function findParentLockfiles(
  projectRoot: string,
  deps: ParentLockfileCheckDeps = {},
): string[] {
  const exists = deps.existsSync ?? defaultExistsSync;
  const homeAbs = resolve(deps.homedir ? deps.homedir() : defaultHomedir());
  const projectAbs = resolve(projectRoot);

  // Guard: only walk inside the home tree. The check exists for the
  // user-home-dir stray-lockfile class of bug (Michal's incident). Projects
  // under /var/www or /opt are outside scope and would only produce noise.
  if (!isAtOrBelow(projectAbs, homeAbs)) return [];

  const found: string[] = [];
  const seen = new Set<string>();
  let dir = dirname(projectAbs);

  while (true) {
    if (seen.has(dir)) break; // symlink loop guard
    seen.add(dir);

    if (!isAtOrBelow(dir, homeAbs)) break;

    for (const name of LOCKFILE_NAMES) {
      const p = join(dir, name);
      try {
        if (exists(p)) found.push(p);
      } catch {
        // Stat errors (EACCES on weird FUSE mounts, etc.) — never crash.
      }
    }

    if (dir === homeAbs) break;

    // Foreign repo boundary above the project — stop here.
    try {
      if (exists(join(dir, '.git'))) break;
    } catch {
      // ignore
    }

    const parent = dirname(dir);
    if (parent === dir) break; // filesystem root
    dir = parent;
  }

  return found;
}

/**
 * Format the lockfile warning shown at the top of `grid dev`. §23 voice:
 * plain, specific, no exclaims, no emoji. Returned as a single string so the
 * caller writes it with one `process.stdout.write`.
 */
export function formatParentLockfileWarning(lockfiles: string[]): string {
  if (lockfiles.length === 0) return '';
  const lines: string[] = [];
  lines.push('Warning: lockfiles found above your project root.');
  for (const lf of lockfiles) lines.push(`  - ${lf}`);
  lines.push(
    "Next.js 15 picks the shallowest lockfile as the workspace root and scans that tree for module tracing. An unintended pick can OOM your dev server under load.",
  );
  lines.push('Fix one of these:');
  lines.push('  - remove the stray lockfile if it does not belong:');
  lines.push(`      rm ${lockfiles[0]}`);
  lines.push('  - pin the workspace root in services/<svc>/next.config.js:');
  lines.push('      module.exports = { outputFileTracingRoot: __dirname };');
  return lines.join('\n') + '\n';
}

/** `child` is `parent` itself, or a descendant of `parent`. */
function isAtOrBelow(child: string, parent: string): boolean {
  if (child === parent) return true;
  // Trailing separator so `/Users/mich` does NOT match `/Users/michal`.
  const prefix = parent.endsWith('/') ? parent : parent + '/';
  return child.startsWith(prefix);
}
